class EngravingComponent extends HTMLElement {
  /** Initialize the engraving data and selects other necessary components. */
  constructor() {
    /** Initialize HTMLElement class */
    super();

    /** Get the engraving data from the DOM. */
    this.engravingData = this.getEngravingData();

    /** Get the current product engraving options. */
    this.currentProductEngravingOptions =
      this.engravingData?.currentProductEngravingOptions;

    /** Engraving form actions */
    this.engravingFormActions = [
      "add-product-and-engraving",
      "add-just-engraving",
      "edit-engraving",
    ];

    /** Get cart drawer component */
    this.cart = document.querySelector("cart-drawer");

    /** Get PDP product form component */
    this.productForm = document.querySelector("product-form");

    /** Get product form add to cart button */
    this.productFormATC = this.productForm?.querySelector(
      `product-form button[type="submit"]`
    );

    this.currentProductProperties = {};
  }

  /** Initialize the component elements and events. */
  connectedCallback() {
    const initializeControl = ({ isTreatment = false } = {}) => {
      if (!this.isConnected || this.controlInitialized) return;
      this.controlInitialized = true;
      this.initElements();
      this.attachEventListeners();
      if (!isTreatment) this.loadMainProductEngravingButton();
      this.loadTitleAndPrice();
    };

    const gate = window.OGEngravingExperiment;
    if (gate?.whenReady) {
      gate.whenReady(initializeControl);
    } else {
      initializeControl();
    }
  }

  /** Remove elements callbacks */
  disconnectedCallback() {
    if (!this.controlInitialized) return;
    this.closeModalBtn?.removeEventListener(
      "click",
      this.handleCloseModalClick.bind(this)
    );

    this.addWatchWithoutEngravingBtn.removeEventListener(
      "click",
      this.addWatchWithoutEngraving.bind(this)
    );

    this.form.removeEventListener("submit", this.formSubmitHandler.bind(this));

    this.mainProductOpenModalBtn?.removeEventListener(
      "click",
      this.handleOpenModalClick.bind(this)
    );
  }

  /** Initializes the component elements. */
  initElements() {
    /** Engraving modal element */
    this.modal = this.querySelector("#engraving-component--modal");

    /** Engraving modal form element */
    this.form = this.querySelector("#engraving-component--form");

    /** Engraving modal form line item key */
    this.lineItemKeyInput = this.form.querySelector(
      "#engraving-component--line-item-key"
    );

    /** Engraving modal form action  dropdown */
    this.engravingFormActionDropdown = this.form.querySelector(
      "#engraving-component--form-action"
    );

    /** Engraving modal title element */
    this.engravingTitle = this.modal.querySelector(
      "#engraving-component--header-title"
    );

    /** Engraving modal price element */
    this.engravingPrice = this.modal.querySelector(
      "#engraving-component--header-price"
    );

    /** Engraving modal close button element */
    this.closeModalBtn = this.querySelector("#engraving-component--close");

    /** Engraving modal add watch with engraving button element */
    this.addWatchAndEngravingBtn = this.querySelector(
      "#add-watch-and-engraving"
    );

    /** Engraving modal add watch without engraving element */
    this.addWatchWithoutEngravingBtn = this.querySelector(
      "#add-watch-without-engraving"
    );

    /** PDP form open engraving modal button element */
    this.mainProductOpenModalBtn = document.querySelector(
      "#main-product--open-engraving-modal"
    );
  }

  /** Attaches event listeners for modal interactions */
  attachEventListeners() {
    /** Engraving modal close modal button event handler */
    this.closeModalBtn?.addEventListener(
      "click",
      this.handleCloseModalClick.bind(this)
    );

    this.addWatchAndEngravingBtn = this.querySelector(
      "#add-watch-and-engraving"
    );

    /** Engraving modal add watch without engraving button event handler */
    this.addWatchWithoutEngravingBtn.addEventListener(
      "click",
      this.addWatchWithoutEngravingHandler.bind(this)
    );

    /** Engraving modal form submit event handler */
    this.form.addEventListener("submit", this.formSubmitHandler.bind(this));

    /** PDP form open engraving modal button event handler */
    this.mainProductOpenModalBtn?.addEventListener(
      "click",
      this.handleOpenModalClick.bind(this)
    );

    // Event listener for cart item add engraving action
    document.addEventListener("cartItemAddEngraving", (event) => {
      if (event.detail?.useTreatmentEngraving) return;
      const { currentProductEngravingOptions, productProperties } =
        event.detail;
      const { engravingTitle, engravingPriceFormatted } =
        currentProductEngravingOptions[0];

      this.currentProductEngravingOptions = currentProductEngravingOptions;
      this.engravingFormActionDropdown.value = this.engravingFormActions[1];

      this.loadTitleAndPrice(engravingTitle, engravingPriceFormatted);
      this.lineItemKeyInput.value = event.detail.lineItemKey || event.detail.productVariantId;
      this.showEngravingModal(true);

      this.currentProductProperties = productProperties;
      this.prefillLines({});
    });

    // Event listener for cart item edit engraving action
    document.addEventListener("cartItemEditEngraving", (event) => {
      if (event.detail?.useTreatmentEngraving) return;
      const {
        currentProductEngravingOptions,
        engravingData,
        engravingProperties,
        productProperties,
      } = event.detail;

      const { engravingTitle, engravingPriceFormatted } =
        currentProductEngravingOptions.filter(
          (el) => el.engravingType === engravingData.engravingType
        )[0];

      this.currentProductEngravingOptions = currentProductEngravingOptions;

      this.engravingFormActionDropdown.value = this.engravingFormActions[2];

      this.loadTitleAndPrice(engravingTitle, engravingPriceFormatted);
      this.lineItemKeyInput.value = event.detail.lineItemKey || event.detail.productVariantId;
      this.showEngravingModal(true);

      this.currentProductProperties = productProperties;
      this.prefillLines(engravingProperties);
    });
  }

  /** Handles form submission and adds engraving details to the cart.
   * @param {Event} event - The form submit event. */
  formSubmitHandler(event) {
    // Prevent form submission
    event.preventDefault();

    // Get product form data
    const engravingFormData = this.getFormData(event);

    // Get selected engraving type
    const selectedEngraving = this.currentProductEngravingOptions.find(
      (el) => el.engravingType === engravingFormData["engraving type"]
    );

    // Get watch variant id
    const watchVariantId = this.getWatchVariantId();

    // If engraving form action is "Add"
    if (
      engravingFormData["engraving-form-action"] === "add-product-and-engraving"
    ) {
      // Build cart data to add product and engraving
      const cartData = {
        id: watchVariantId,
        quantity: 1,
        properties: {
          _engraving_service_variant_id: `gid://shopify/ProductVariant/${selectedEngraving.engravingId}`,
          _engraving_service_price: `${selectedEngraving.engravingPrice}`,
          _Line1: engravingFormData["engraving-line-1"] || "",
          _Line2: engravingFormData["engraving-line-2"] || "",
          _Line3: engravingFormData["engraving-line-3"] || "",
          _engraving_type: `${selectedEngraving.engravingType}`,
        },
        sections: this.cart.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      };

      // Call addProductAndEngraving function
      this.addProductAndEngraving(cartData);

      return;
    }

    // If engraving form action is "add just engraving" or "edit engraving"
    if (
      engravingFormData["engraving-form-action"] === "add-just-engraving" ||
      engravingFormData["engraving-form-action"] === "edit-engraving"
    ) {
      // Build data to edit cart properties
      const cartData = {
        id: `${watchVariantId}`,
        properties: {
          ...this.currentProductProperties,
          _engraving_service_variant_id: `gid://shopify/ProductVariant/${selectedEngraving.engravingId}`,
          _engraving_service_price: `${selectedEngraving.engravingPrice}`,
          _Line1: engravingFormData["engraving-line-1"] || "",
          _Line2: engravingFormData["engraving-line-2"] || "",
          _Line3: engravingFormData["engraving-line-3"] || "",
          _engraving_type: `${selectedEngraving.engravingType}`,
        },
        sections: this.cart.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      };
      this.addEngravingProperties(cartData);
    }
  }

  /** Adds the product to the cart via API.
   * @param {Object} cartData - The cart data object. */
  async addEngravingProperties(cartData) {
    // Disable the addWatchAndEngravingBtn button
    this.addWatchAndEngravingBtn.disabled = true;

    try {
      // Send request
      const request = await fetch(
        window.Shopify.routes.root + "cart/change.js",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(cartData),
        }
      );

      // Await the request response
      const response = await request.json();

      // Render the cart drawer with the response
      this.renderCartDrawer(response);

      // Hide engraving modal
      this.showEngravingModal(false);
    } catch (error) {
      // Print error in the console
      console.log(`❌ Engraving Error | ${error}`);
    } finally {
      // Enable the addWatchAndEngravingBtn button
      this.addWatchAndEngravingBtn.disabled = false;

      // Reset engraving form action to add product and engraving
      this.engravingFormActionDropdown.value = this.engravingFormActions[0];

      // Reset current product engraving  options based on the engraving data
      this.currentProductEngravingOptions =
        this.engravingData?.currentProductEngravingOptions;

      // Reset title and price
      this.loadTitleAndPrice();

      // Clear line item key
      this.lineItemKeyInput.value = "";
    }
  }

  /** Adds the product to the cart via API.
   * @param {Object} cartData - The cart data object. */
  async addProductAndEngraving(cartData) {
    // Disable the addWatchAndEngravingBtn button
    this.addWatchAndEngravingBtn.disabled = true;

    try {
      // Send request
      const request = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cartData),
      });

      // Await the request response
      const response = await request.json();

      // Render the cart drawer with the response
      this.renderCartDrawer(response);

      // Hide engraving modal
      this.showEngravingModal(false);
    } catch (error) {
      // Print error in the console
      console.log(`❌ Engraving Error | ${error}`);
    } finally {
      // Enable the addWatchAndEngravingBtn button
      this.addWatchAndEngravingBtn.disabled = false;
    }
  }

  /** Get the current watch variant ID.
   * @returns {string} The watch variant ID. */
  getWatchVariantId() {
    return (
      this.lineItemKeyInput.value ||
      this.productForm?.querySelector('input[name="id"]').value
    );
  }

  /** Get the engraving form data.
   * @param {Event} event form submission event
   * @returns {Object} The form data. */
  getFormData(event) {
    const formData = new FormData(event.target);
    return Object.fromEntries(formData.entries());
  }

  /** Handles the click event on the  addWatchWithoutEngraving button*/
  addWatchWithoutEngravingHandler() {
    // Clicks the main product form add to cart button
    this.productFormATC.click();

    // Close the engraving modal
    this.showEngravingModal(false);
  }

  /** Handles the close modal button click event.
   * @param {Event} event - The click event. */
  handleCloseModalClick(event) {
    if (!event.target.closest("#engraving-component--close")) return;
    this.showEngravingModal(false);
  }

  /** Handles the open modal button click event.
   * @param {Event} event - The click event. */
  handleOpenModalClick(event) {
    if (!event.target.closest("button[data-open-engraving-modal]")) return;
    this.currentProductEngravingOptions = this.engravingData.currentProductEngravingOptions;
    if (!this.currentProductEngravingOptions.length) return;
    this.loadTitleAndPrice();
    this.showEngravingModal(true);
    this.engravingFormActionDropdown.value = this.engravingFormActions[0];
    this.lineItemKeyInput.value = "";
    this.prefillLines({});
    const mainProductOpenModalEvent = new CustomEvent(
      "mainProductOpenModalEvent"
    );
    document.dispatchEvent(mainProductOpenModalEvent);
  }

  /** Toggles the engraving modal visibility.
   * @param {boolean} flag - Whether to show or hide the modal. */
  showEngravingModal(flag) {
    this.modal.dataset.showEngravingModal = flag;
  }

  /** Restore original cart strings without decoding customer-entered entities. */
  prefillLines(properties = {}) {
    for (let line = 1; line <= 3; line++) {
      const input = this.form.querySelector(`#engraving-line-${line}`);
      if (input) input.value = properties[`_Line${line}`] || "";
    }
  }

  /** Loads the main product engraving button based on the engraving option. */
  loadMainProductEngravingButton() {
    if (!this.mainProductOpenModalBtn) return;
    const eligible = Boolean(this.getDefaultEngravingOption()?.engravingType);
    this.mainProductOpenModalBtn.textContent = "Add Engraving";
    this.mainProductOpenModalBtn.dataset.engravingEligible = String(eligible);
    this.mainProductOpenModalBtn.style.display = eligible ? "block" : "none";
  }

  /** Loads the engraving option title and price.
   * @param {String} titleParam Engraving modal title
   * @param {String} priceParam Engraving modal price */

  loadTitleAndPrice(titleParam, priceFormattedParam) {
    // Check if titleParam and priceFormattedParam have content
    if (titleParam && priceFormattedParam) {
      this.engravingTitle.innerHTML = titleParam;
      this.engravingPrice.textContent = priceFormattedParam;
      this.addWatchAndEngravingBtn.textContent = "Add Engraving";
      return;
    }

    // Guard clause to prevent loading undefined data
    if (this.getDefaultEngravingOption().length === 0) return;

    // Get title and price of the default engraving option
    const { engravingTitle, engravingPriceFormatted } = this.getDefaultEngravingOption();

    // Updates title and price elements content
    this.engravingTitle.innerHTML = engravingTitle;
    this.engravingPrice.textContent = engravingPriceFormatted;
    this.addWatchAndEngravingBtn.textContent = "Add Engraving";
  }

  /** Retrieves engraving data from the DOM.
   * @returns {Object} Parsed engraving data. */
  getEngravingData() {
    return JSON.parse(
      document.querySelector("#engraving-options-data").textContent
    );
  }

  /** Retrieves the default engraving option.
   * @returns {Object} Default engraving option. */
  getDefaultEngravingOption() {
    return this.currentProductEngravingOptions[0] || [];
  }

  /** Renders the cart drawer with the updated cart response.
   * @param {Object} response - The cart response object. */
  renderCartDrawer(response) {
    const { sections } = response;

    const parsedResponse = {
      id: response.id,
      sections,
    };

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: "engraving-form",
      productVariantId: parsedResponse.id,
      cartData: parsedResponse,
    });

    this.cart.renderContents(parsedResponse);

    if (this.cart && this.cart.classList.contains("is-empty"))
      this.cart.classList.remove("is-empty");
  }
}

customElements.define("engraving-component", EngravingComponent);
